/**
 * App-wide success screen for settings saves. Toasts and SuccessCelebration
 * call ``showSuccessScreen``; ``SuccessScreenHost`` (mounted once in
 * ToastProvider, outside the router) renders it, so it survives the
 * navigation back to All Settings that many settings saves do.
 */

const listeners = new Set();
let counter = 0;

/** Same areas smartBack treats as Settings (settings sidebar pages). */
const SETTINGS_PREFIXES = [
  "/settings",
  "/salary-v2",
  "/statutory",
  "/tools",
  "/payrollhome",
  "/attendance/configuration",
  "/attendance/setup",
];

export function isSettingsAreaPath(pathname) {
  const path = ((pathname || "/").replace(/\/+$/, "") || "/").toLowerCase();
  return SETTINGS_PREFIXES.some((p) => path === p || path.startsWith(`${p}/`));
}

// A completed save / change — not checks, previews, exports or "now save it" hints.
const SAVE_WORDS =
  /\b(saved|updated|created|added|deleted|removed|archived|restored|recorded|assigned|completed?|finished|configured|submitted)\b|successfully/i;

export function isSaveMessage(text) {
  return typeof text === "string" && SAVE_WORDS.test(text);
}

/** "Rules saved. Calendar now uses them." → title "Rules saved!" + message "Calendar now uses them." */
export function splitSuccessText(text) {
  const clean = String(text || "").replace(/^[\s✓✔]+/, "").trim();
  const m = clean.match(/^(.+?[.!])\s+(.+)$/s);
  const head = (m ? m[1] : clean).trim();
  const rest = m ? m[2].trim() : "";
  const title = /[.!]$/.test(head) ? `${head.slice(0, -1)}!` : /…$/.test(head) ? head : `${head}!`;
  return { title, message: rest };
}

/** True when a success toast should become the success screen instead. */
export function shouldUseSuccessScreen(message, options = {}) {
  if (options.screen === false) return false;
  if (options.screen === true) return true;
  const pathname = typeof window !== "undefined" ? window.location.pathname : "";
  return isSettingsAreaPath(pathname) && isSaveMessage(message);
}

let last = { title: "", at: 0 };

export function showSuccessScreen(text) {
  const { title, message } = splitSuccessText(text);
  // One save can fire both a toast and a SuccessCelebration: show it once.
  const now = Date.now();
  if (title === last.title && now - last.at < 1500) return;
  last = { title, at: now };
  const item = {
    id: ++counter,
    title,
    message,
    redirecting: /\b(redirecting|returning|continuing)\b/i.test(message),
  };
  listeners.forEach((fn) => fn(item));
}

export function subscribeSuccessScreen(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}
